import { HTMLAttributes, ReactNode } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { taskStatusBadge } from '../../../lib/task-status';
import styles from './Badge.module.css';

// THE status/category label for the whole app — Figma "⚠️ Symphony DS" → Badge
// Symphony DS (node 434:337). Never hand-roll a small filled pill with 12px semibold
// text; see CLAUDE.md → "Badges".
//
// The design is Style × Size, plus the two icon slots, so those are the prop names:
//   variant  → Figma *Style*  (its axis is called Style, which `style` already means
//              in React — the value names are the design system's own)
//   size     → Figma *Size*   (tiny 20 · small 24 · medium 28)
//   startIcon / endIcon → Figma *Show prefix* / *Show suffix*
//
// A badge takes a STYLE, never a colour: each Style owns a `--ds-badge-*` fill/ink/
// border triple, so hover-free states and dark mode are the token layer's problem and
// no call site can invent a hue. It is also not interactive — a clickable pill is a
// <Button>, and a count on a tab is a counter, not a Badge.
export type BadgeVariant =
  | 'waiting-on-a-reply'
  | 'waiting-on-you'
  | 'in-progress'
  | 'done'
  | 'paused'
  | 'failed'
  | 'connected'
  | 'active'
  | 'agents'
  | 'agent-profession'
  | 'this-week'
  | 'next-week'
  | 'in-three-weeks';

export type BadgeSize = 'tiny' | 'small' | 'medium';

export interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'color'> {
  /** Figma's Style. The design system's grey fill is `waiting-on-a-reply`, its only outline is `agent-profession`. */
  variant?: BadgeVariant;
  /** Figma's Size. Default `tiny` — the size every badge in the app draws at. */
  size?: BadgeSize;
  label?: ReactNode;
  /** Figma's "Show prefix" — a 16px leading icon. */
  startIcon?: ReactNode;
  /** Figma's "Show suffix" — a 16px trailing icon. */
  endIcon?: ReactNode;
  children?: ReactNode;
}

export function Badge({ variant = 'waiting-on-a-reply', size = 'tiny', label, startIcon, endIcon, className, children, ...rest }: BadgeProps) {
  const classes = [styles.root, styles[`variant-${variant}`], styles[`size-${size}`], className].filter(Boolean).join(' ');

  return (
    <span className={classes} {...rest}>
      {startIcon != null && (
        <span aria-hidden className={styles.icon}>
          {startIcon}
        </span>
      )}
      <span className={styles.label}>{label ?? children}</span>
      {endIcon != null && (
        <span aria-hidden className={styles.icon}>
          {endIcon}
        </span>
      )}
    </span>
  );
}

// The task-status badge: reads the shared status→{labelKey,variant} map and localizes
// the label at the render site (mirrors the native TaskScreen). The map hands out a
// STYLE, never a colour — see lib/task-status.ts.
export function StatusBadge({
  status,
  size,
  className,
  detail,
}: {
  status: string;
  size?: BadgeSize;
  className?: string;
  // The task-DETAIL screen sets this so IN_REVIEW reads "In review"; cards leave it off (bucket label).
  detail?: boolean;
}) {
  const { t } = useTranslation();
  const badge = taskStatusBadge(status, { detail });
  return <Badge variant={badge.variant} label={t(badge.labelKey)} size={size} className={className} />;
}
